import { useState, useCallback } from "react";
import moment from "moment";
import { usePolicyVersionList } from "repositories/office-policy-management-repository";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";
export const sortingVariablesForPoliciesListing = {
  DocumentName: 1,
  VersionNumber: 2,
  UploadedBy: 3,
  DateUploaded: 4,
  StartDateDueDate: 5,
  Status: 6,
};

/** Default date range: 1 Jan current year to today (latest version created in current year). */
export const defaultDocumentListingDateRange = () => ({
  fromDate: null,
  toDate: null,
});

/**
 * Hook for Policy Document Listing page: policy versions list for a folder with filters
 * (date range, offices, status). Date range filter requires both start and end; end must be after start.
 * @param {string|number} officePolicyFolderId - Policy folder id from route
 */
export default function usePolicyDocumentListing(officePolicyFolderId) {
  const {
    isOwner: isAccountOwner,
    effectiveOwnerId: accountOwnerId,
    isStaff,
    dataAccessValues,
  } = useAccountOwnerCommon();

  const [officeFilter, setOfficeFilter] = useState([]);
  const [staffFilter, setStaffFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [dateRange, setDateRange] = useState(defaultDocumentListingDateRange());
  const [appliedOfficeFilter, setAppliedOfficeFilter] = useState([]);
  const [appliedStaffFilter, setAppliedStaffFilter] = useState([]);
  const [appliedStatusFilter, setAppliedStatusFilter] = useState([]);
  const [appliedDateRange, setAppliedDateRange] = useState(
    defaultDocumentListingDateRange(),
  );
  const [searchValue, setSearchValue] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [sortBy, setSortBy] = useState(sortingVariablesForPoliciesListing.DocumentName);
  const [isDescending, setIsDescending] = useState(true);

  const fromDate = appliedDateRange?.fromDate ?? null;
  const toDate = appliedDateRange?.toDate ?? null;
  const dateUploadedFrom =
    fromDate &&
    (fromDate instanceof Date
      ? moment(fromDate).format("YYYY-MM-DD")
      : fromDate);
  const dateUploadedTo =
    toDate &&
    (toDate instanceof Date ? moment(toDate).format("YYYY-MM-DD") : toDate);

  const canFetch =
    !!officePolicyFolderId &&
    !!accountOwnerId &&
    (isAccountOwner || (isStaff && !!dataAccessValues?.isAdmin));

  const sortDirection = isDescending ? 1 : 2;
  const { data, isLoading, refetch } = usePolicyVersionList(
    officePolicyFolderId,
    dateUploadedFrom,
    dateUploadedTo,
    appliedOfficeFilter,
    appliedStatusFilter,
    searchValue,
    appliedStaffFilter,
    sortBy,
    sortDirection,
    { enabled: canFetch },
  );

  const items = data?.items ?? [];
  const pagination = data?.pagination ?? {};
  const totalItems = pagination?.totalItems ?? items.length;

  const resetFilter = useCallback(() => {
    setOfficeFilter([]);
    setStaffFilter([]);
    setStatusFilter([]);
    setDateRange(defaultDocumentListingDateRange());
    setAppliedOfficeFilter([]);
    setAppliedStaffFilter([]);
    setAppliedStatusFilter([]);
    setAppliedDateRange(defaultDocumentListingDateRange());
    setSortBy(sortingVariablesForPoliciesListing.DocumentName);
    setIsDescending(true);
  }, []);

  /** Apply filters. Date range: both from and to required; end must be > start. */
  const handleApplyFilters = useCallback(() => {
    const from = dateRange?.fromDate;
    const to = dateRange?.toDate;
    const hasValidDateRange =
      from && to && moment(to).isSameOrAfter(moment(from), "day");
    setAppliedOfficeFilter(officeFilter || []);
    setAppliedStaffFilter(staffFilter || []);
    setAppliedStatusFilter(statusFilter || []);
    setAppliedDateRange(
      hasValidDateRange ? { fromDate: from, toDate: to } : appliedDateRange,
    );
  }, [dateRange, officeFilter, staffFilter, statusFilter, appliedDateRange]);

  const isFilterApply =
    appliedOfficeFilter?.length > 0 ||
    appliedStaffFilter?.length > 0 ||
    appliedStatusFilter?.length > 0;

  const handleSearchTerm = useCallback((e) => {
    const value = e?.target?.value ?? "";
    setInputValue(value);
    if (value.length >= 3) {
      setSearchValue(value);
    } else if (value.length === 0) {
      setSearchValue("");
    }
  }, []);

  return {
    isLoading,
    items,
    totalItems,
    inputValue,
    handleSearchTerm,
    searchValue,
    officeFilter,
    setOfficeFilter,
    staffFilter,
    setStaffFilter,
    statusFilter,
    setStatusFilter,
    dateRange,
    setDateRange,
    appliedOfficeFilter,
    appliedStaffFilter,
    appliedStatusFilter,
    appliedDateRange,
    isFilterApply,
    handleApplyFilters,
    resetFilter,
    accountOwnerId,
    isStaff,
    refetch,
    sortBy,
    setSortBy,
    isDescending,
    setIsDescending,
  };
}
